import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { find, get } from 'lodash-es';

import ReportsTable from './ReportsTable';
import VersionsHistoryModal from '../ApptopiaExports/VersionsHistoryModal';

import {
  fetchCustomReportsData,
  fetchVersionsData,
  changeCustomReportsPerPage,
} from '../../../actions';
import {
  customReportsSettingsSelector,
  customReportsFiltersSelector,
  customReportsSelector,
  versionsSelector,
  versionsSettingsSelector,
} from '../../../selectors';

import { initParams, permissions } from '../../../configs';
import { useAppCable } from '../../../../../hooks';

const { hasFirstVisitAlert } = initParams;

const ReportsTableSection = () => {
  const dispatch = useDispatch();

  const settings = useSelector(customReportsSettingsSelector);
  const filters = useSelector(customReportsFiltersSelector);
  const tableData = useSelector(customReportsSelector);
  const versions = useSelector(versionsSelector);
  const versionsSettings = useSelector(versionsSettingsSelector);

  const [currentCustomReportId, setCurrentCustomReportId] = useState(0);

  const getCurrentReportVersions = (reportId) =>
    find(versions, { custom_report_id: reportId.toString() });

  const getCurrentReport = (reportId) => find(tableData, { id: reportId });

  const handleVersionsHistoryRequest = (reportId) => {
    dispatch(fetchVersionsData({ custom_report_id: reportId }));
  };
  const handleFiltersChange = (filtersObj) => {
    dispatch(fetchCustomReportsData(filtersObj));
  };

  const handlePerPageChange = useCallback((value) => {
    dispatch(changeCustomReportsPerPage(value));
  }, []);

  const handleVersionsHistoryOpen = (reportId) => {
    setCurrentCustomReportId(reportId);
  };

  const handleDownloadEventReceived = (event) => {
    const { custom_s3_report_id } = event;
    if (find(tableData, { id: custom_s3_report_id })) {
      handleFiltersChange(filters);
    }
    if (currentCustomReportId.toString() === custom_s3_report_id.toString()) {
      handleVersionsHistoryRequest(custom_s3_report_id);
    }
  };

  useAppCable(
    { channel: 'CustomS3ReportsChannel' },
    {
      onReceived: handleDownloadEventReceived,
    }
  );

  useEffect(() => {
    if (permissions.custom_reports) {
      dispatch(fetchCustomReportsData());
    }
  }, []);

  useEffect(() => {
    if (currentCustomReportId !== 0 && !getCurrentReportVersions(currentCustomReportId)) {
      handleVersionsHistoryRequest(currentCustomReportId);
    }
  }, [currentCustomReportId]);

  const versionHistory = getCurrentReportVersions(currentCustomReportId) || {};

  return (
    <div className="custom-exports custom-reports">
      <section className="custom-exports__head">
        <h4 className="custom-exports__head-heading h4">Apptopia-generated exports</h4>
        <p className="custom-exports__head-subheading">
          Exports requested by your company, produced by the Apptopia data team
        </p>
      </section>
      <section className="custom-exports__table">
        <ReportsTable
          tableData={tableData}
          filters={filters}
          settings={settings}
          hasFirstVisitAlert={hasFirstVisitAlert}
          onFiltersChange={handleFiltersChange}
          onVersionsHistoryRequest={handleVersionsHistoryOpen}
          onPageChange={handleFiltersChange}
          onPerPageChange={handlePerPageChange}
        />
        {!!currentCustomReportId && (
          <VersionsHistoryModal
            versionHistory={versionHistory}
            pending={get(versionsSettings, 'pending')}
            open={parseInt(currentCustomReportId, 10) > 0}
            handleClose={() => handleVersionsHistoryOpen(0)}
            report={getCurrentReport(currentCustomReportId)}
          />
        )}
      </section>
    </div>
  );
};

export default ReportsTableSection;
